import { Link } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import { useEffect } from 'react';
import NewArrivalsSection from '@/components/feature/NewArrivalsSection';

export default function Home() {
  const { addToCart } = useCart();

  useEffect(() => {
    // SEO JSON-LD pour la page d'accueil
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Les Carnets de Lolotte",
      "description": "Boutique artisanale de décorations de Noël en papier plié. Créations uniques faites main avec amour.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`,
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/boutique?search={search_term_string}`,
        "query-input": "required name=search_term_string"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Les Carnets de Lolotte",
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/e5a40b4f7b52fcfa548a8f38ffe25152.png"
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    // Mise à jour des meta tags
    document.title = "Les Carnets de Lolotte – Boutique artisanale de décorations de Noël";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Boutique artisanale Les Carnets de Lolotte. Créations uniques en papier plié : anges de Noël, bougies décoratives, sapins artisanaux. Fait main avec amour en France. Livraison soignée.');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const featuredProducts = [
    {
      id: '2',
      name: 'Ange "Joyeux Cœur"',
      price: 4,
      image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/9ca0f2dee5922ad907a452879600467c.png',
      description: 'L\'ange de la joie et de l\'amour ❤️',
      link: '/produit-ange-joyeux-coeur',
    },
    {
      id: 'bougie-petite-avec-led',
      name: 'Sapin de noël ✨',
      price: 20,
      image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/b25886b6d5302fa4fce5dc966e858d39.png',
      description: "Chaque pli raconte une histoire...",
      link: '/produit-bougie-livre-plie',
    },
    {
      id: 'livre-personnalise-photo',
      name: 'Livre plié personnalisé avec photo',
      price: 40,
      image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/46c9733f464f1d0031a3113d5ad95597.png',
      description: 'Création unique avec votre photo préférée',
      link: '/personnalisations',
    },
  ];

  const handleAddToCart = (product: typeof featuredProducts[0]) => {
    addToCart(
      {
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        description: product.description,
        link: product.link,
      },
      1
    );

    const button = document.activeElement as HTMLButtonElement;
    if (button) {
      const originalText = button.innerHTML;
      button.innerHTML = '<i class="ri-check-line mr-2"></i>Ajouté !';
      button.disabled = true;
      setTimeout(() => {
        button.innerHTML = originalText;
        button.disabled = false;
      }, 1500);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      <header>
        <h1 className="sr-only">Les Carnets de Lolotte</h1>
      </header>
      
      <main>
        <section
          className="relative min-h-screen flex items-center justify-center bg-cover bg-center px-4 sm:px-6 lg:px-8"
          style={{
            backgroundImage: `linear-gradient(rgba(248,237,234,0.9), rgba(248,237,234,0.9)), url('https://public.readdy.ai/ai/img_res/f9329649c22df9df27c4fad6f053be59.jpg')`,
          }}
        >
          <div className="text-center max-w-5xl mx-auto w-full">
            <h2
              className="text-4xl sm:text-6xl md:text-7xl lg:text-9xl font-bold mb-6 sm:mb-8 text-[#C49B86] leading-tight"
              style={{ fontFamily: 'Allura, cursive' }}
            >
              Les Carnets de Lolotte ✨
            </h2>
            
            <p
              className="text-xl sm:text-2xl md:text-3xl mb-8 sm:mb-10 text-[#6B5E5A] leading-relaxed px-4 font-medium"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Les Carnets de Lolotte proposent des créations artisanales en livres pliés, entre décoration poétique et cadeaux chargés de sens.
            </p>
            
            <div className="mb-8 sm:mb-12">
              <p
                className="text-lg sm:text-xl md:text-2xl lg:text-3xl mb-4 sm:mb-6 text-[#6B5E5A] leading-relaxed px-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                ✨ Quand les pages se plient, les histoires s'envolent...
              </p>
              <p
                className="text-base sm:text-lg md:text-xl lg:text-2xl mb-3 sm:mb-4 text-[#6B5E5A] leading-relaxed px-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Bienvenue dans l'univers des <strong>Carnets de Lolotte</strong>,
              </p>
              <p
                className="text-base sm:text-lg md:text-xl lg:text-2xl text-[#6B5E5A] leading-relaxed px-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                où chaque création en papier devient un souffle de poésie et de lumière. 🌸
              </p>
            </div>

            <Link
              to="/boutique"
              className="inline-block bg-[#C49B86] text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg lg:text-xl hover:bg-[#B97A72] transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Découvrir la boutique
            </Link>
          </div>
        </section>

        {/* Section Nouveautés */}
        <NewArrivalsSection />

        {/* Section créations coup de cœur */}
        <section className="py-12 sm:py-16 lg:py-20 bg-[#FAF9F8]" data-product-shop>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16">
              <h3
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#C49B86] mb-4 sm:mb-6"
                style={{ fontFamily: 'Allura, cursive' }}
              >
                💕 Nos créations coup de cœur
              </h3>
              <p
                className="text-base sm:text-lg text-[#6B5E5A] max-w-2xl mx-auto px-4"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Découvrez nos trois créations phares, faites main avec amour et patience
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-12">
              {featuredProducts.map((product) => (
                <article
                  key={product.id}
                  className="bg-white rounded-2xl shadow-lg border-2 border-[#C49B86]/20 overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <div className="aspect-square overflow-hidden cursor-pointer">
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-center hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4 sm:p-6 lg:p-8">
                    <h4
                      className="text-lg sm:text-xl lg:text-2xl font-semibold text-[#6B5E5A] mb-3 sm:mb-4"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {product.name}
                    </h4>
                    <p
                      className="text-[#6B5E5A] text-sm sm:text-base mb-4 sm:mb-6 italic leading-relaxed"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      {product.description}
                    </p>
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-0">
                      <span
                        className="text-2xl sm:text-3xl font-bold text-[#C49B86]"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        {product.price}€
                      </span>
                      <Link
                        to={product.link}
                        className="w-full sm:w-auto bg-[#C49B86] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full hover:bg-[#B97A72] transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base text-center"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Voir le produit
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="text-center mt-12 sm:mt-16">
              <p
                className="text-lg sm:text-xl text-[#6B5E5A] mb-4 sm:mb-6 px-4"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Envie de découvrir toutes nos créations ?
              </p>
              <Link
                to="/boutique"
                className="inline-block bg-transparent text-[#C49B86] px-6 sm:px-8 py-3 rounded-full border-2 border-[#C49B86] hover:bg-[#C49B86] hover:text-white transition-colors whitespace-nowrap cursor-pointer text-base sm:text-lg font-semibold"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Voir toute la boutique
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Section contact / commandes spéciales */}
        <section className="py-12 sm:py-16 lg:py-20 bg-[#EBD2CF]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#C49B86] mb-6 sm:mb-8"
              style={{ fontFamily: 'Allura, cursive' }}
            >
              Une idée, une envie particulière ?
            </h3>
            <div
              className="text-base sm:text-lg lg:text-xl text-[#6B5E5A] leading-relaxed mb-6 sm:mb-8 space-y-4 px-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              <p>J'accepte aussi les commandes personnalisées 💌</p>
              <p>
                Contactez-moi directement ici ou sur Facebook :{' '}
                <a
                  href="https://www.facebook.com/profile.php?id=61582554598991"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#C49B86] hover:underline font-medium"
                >
                  Les Carnets de Lolotte
                </a>
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-block bg-[#C49B86] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg hover:bg-[#B97A72] transition-colors whitespace-nowrap"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Me contacter
            </Link>
          </div>
        </section>

        {/* Paiement sécurisé */}
        <section className="py-8 sm:py-12 bg-[#C49B86] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8">
              <div className="flex items-center space-x-2">
                <i className="ri-shield-check-line text-xl sm:text-2xl"></i>
                <span
                  className="font-semibold text-sm sm:text-base"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Paiement sécurisé via Stripe
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-bank-card-line text-xl sm:text-2xl"></i>
                <span className="text-sm sm:text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>CB</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-truck-line text-xl sm:text-2xl"></i>
                <span className="text-sm sm:text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>Livraison soignée</span>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
